import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Snowflake, BatteryCharging, Lightbulb } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const FRIDGES_HERO = "/products/fridges/731077_2e9336e23caf49a9b86ea2cb7912a68e~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ");

// Already native elsewhere on the site — cross-linked here, not duplicated, so
// this page shows the full range of fridge/drop slides alongside our own EEZI-AWN slides
const FRIDGE_LINKED_PRODUCTS = [
  { name: "EEZI-AWN K9 Fridge Slide — Up To 50 Litres", price: 399, thumb: "/products/fridges/731077_b93ac9c607454b38ac4561dabf5052b0~mv2.jpg", to: "/drawers-kitchens-tables/eezi-awn-k9-fridge-slide-up-to-50l" },
  { name: "EEZI-AWN K9 Fridge Slide — 60 To 90 Litres", price: 549, thumb: "/products/fridges/731077_b93ac9c607454b38ac4561dabf5052b0~mv2.jpg", to: "/drawers-kitchens-tables/eezi-awn-k9-fridge-slide-60-90l" },
  { name: "Quick Pitch Fridge Slides — Three Sizes", price: 695, priceFrom: true, thumb: "/products/drawers/731077_fb65647e1212401f8bd10e3fdd1fa115~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-fridge-slides" },
  { name: "Quick Pitch Drop Fridge Slides — Various Sizes", price: 1599, priceFrom: true, thumb: "/products/drawers/731077_5ca90a7052bf4303ad46ba4e17b1b290~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-drop-fridge-slides" },
  { name: "Bushtech Fridge/Cargo Slide for Jimny", price: 1095, thumb: "/products/jimny/731077_b355ac6560b948d1b23fa949c7315ca9~mv2.jpg", to: "/jimny-store/bushtech-fridge-cargo-slide-jimny" },
];

const SECTIONS = [
  { id: "fridges", label: "National Luna Fridges", heading: "30 YEARS OF COLD, PROVEN IN AFRICA.", categories: ["fridge"] },
  { id: "fridge-slides", label: "Fridge Slides", heading: "ACCESS WITHOUT THE STRAIN.", categories: ["fridge-slide"] },
  { id: "power-lighting", label: "Power & LED Lighting", heading: "STAY OFF-GRID LONGER.", categories: ["power", "lighting"] },
];

const WHY = [
  { icon: Snowflake, t: "Commercial & Medical-Grade", d: "Originally engineered to WHO standards for transporting vaccines across Sub-Saharan Africa — still built to that spec today." },
  { icon: BatteryCharging, t: "Genuinely Low Power Draw", d: "Denser, high-pressure-injected insulation means class-leading 24-hour current draw — no bulky insulated covers needed." },
  { icon: Lightbulb, t: "Proven 4WD Electrics", d: "National Luna power packs & LED lighting are built to the same commercial standard as their fridges." },
];

const priceLabel = (m) => {
  if (m.priceOnRequest) return "REQUEST QUOTE";
  return `${m.priceFrom ? "FROM " : ""}$${fmt(m.price)}`;
};

const Card = ({ m, testid }) => (
  <Link
    to={`/12v-fridges/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">{m.badge}</span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-xl text-[var(--brand-primary)] leading-none">{priceLabel(m)}</p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function FridgesPage() {
  const [products, setProducts] = useState(null); // null = still loading
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/api/products/fridge`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  // The Shop dropdown deep-links to #power-lighting (and similar anchors). Those sections only
  // render once `products` has loaded, so the browser's native hash-scroll can fire too early
  // and land on nothing. Retry the scroll once the section actually exists in the DOM.
  useEffect(() => {
    if (products && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    }
  }, [products, location.hash]);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="fridges-page">
      <SEO
        path="/12v-fridges"
        title="National Luna 12V Fridges, Power & LED Lighting NZ"
        description="National Luna commercial-grade 12V fridge freezers, EEZI-AWN fridge slides, DC40 power packs and IP66 LED touch lights. From $79 to $2,690."
        image={FRIDGES_HERO}
      />

      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={FRIDGES_HERO} alt="National Luna fridge freezer" className="w-full h-full object-cover" style={{ objectPosition: "50% 40%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— National Luna · 12V Fridges & Electrical</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            COMMERCIAL-GRADE COLD.<br />
            <span className="text-[var(--brand-primary)]">MEDICAL-GRADE</span> RELIABILITY.
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              30+ years of world-leading commercial-grade refrigeration from South Africa — now backed by fridge slides, portable power and LED lighting built to the same standard.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              {SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} data-testid={`fridges-cta-${s.id}`} className="btn-secondary">{s.label}</a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} data-testid={`fridges-why-${i}`} className="bg-[var(--bg-main)] p-6 md:p-8">
                <Icon size={22} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
                <h3 className="font-display text-lg md:text-xl text-white mb-2">{w.t}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{w.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {SECTIONS.map((section, si) => {
        const items = products.filter((m) => section.categories.includes(m.type));
        const hasLinked = section.id === "fridge-slides" && FRIDGE_LINKED_PRODUCTS.length > 0;
        if (items.length === 0 && !hasLinked) return null;
        return (
          <section
            key={section.id}
            id={section.id}
            className={`container-x pb-16 md:pb-20 scroll-mt-24 ${si > 0 ? "pt-16 md:pt-20 border-t border-[var(--border-default)]" : ""}`}
          >
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="overline mb-3">— {section.label}</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">{section.heading}</h2>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((m, i) => <Card key={m.slug} m={m} testid={`${section.id}-item-${i}`} />)}
            </div>
            {section.id === "fridge-slides" && hasLinked && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {FRIDGE_LINKED_PRODUCTS.map((p, i) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`fridges-linked-${i}`}
                    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
                  >
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img src={p.thumb} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                        <h3 className="font-display text-lg md:text-xl text-white leading-tight">{p.name.toUpperCase()}</h3>
                        <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-3">{p.priceFrom ? "FROM " : ""}${fmt(p.price)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
